/**
 * The sound a new order makes. It is synthesised rather than loaded, so there is no file
 * to go missing and nothing to fetch — a kitchen screen on a bad connection still
 * announces an order.
 */

let context: AudioContext | null = null

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null
  const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null
  context ??= new Ctor()
  return context
}

/**
 * Browsers refuse to make noise until the person has touched the page. A kitchen screen
 * is opened once and then left alone, so the first touch is used to wake the sound and
 * it stays awake for the rest of the shift.
 */
export function unlockSound() {
  const ctx = audioContext()
  if (ctx && ctx.state === 'suspended') void ctx.resume()
}

/** Two short notes, which carries across a kitchen better than one long one. */
export function playNewOrderChime(volumePercent: number) {
  const ctx = audioContext()
  if (ctx?.state !== 'running') return
  const volume = Math.max(0, Math.min(100, volumePercent)) / 100
  if (volume === 0) return
  const start = ctx.currentTime
  for (const [index, frequency] of [880, 1174.7].entries()) {
    const at = start + index * 0.16
    const oscillator = ctx.createOscillator()
    const gain = ctx.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.value = frequency
    // Shaped rather than switched, because a square edge on a speaker is a click.
    gain.gain.setValueAtTime(0, at)
    gain.gain.linearRampToValueAtTime(volume * 0.3, at + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.15)
    oscillator.connect(gain).connect(ctx.destination)
    oscillator.start(at)
    oscillator.stop(at + 0.16)
  }
}

/**
 * Which orders are new since last time. The first look is never announced — opening the
 * screen to a full rail should not set off a chime for every order already waiting.
 */
export function newSlipIds(previous: Set<string> | null, current: string[]): string[] {
  if (previous === null) return []
  return current.filter((id) => !previous.has(id))
}
